import { z } from "zod";
import { ApiMoney, TaskAddressView, TaskAnswerView } from "./tasks";

/**
 * Teklif iste (K-059): müşteri en çok 3 hizmet veren seçer, hizmet verenler 24 saatte toplam sabit fiyat +
 * tahmini süre + mesajla teklif verir, müşteri 48 saat içinde birini kabul eder.
 */
export const MAX_QUOTE_PROVIDERS = 3;

/** POST /v1/tasks/:id/quote-request */
export const QuoteRequestBody = z.object({
  providerIds: z
    .array(z.string().uuid())
    .min(1)
    .max(MAX_QUOTE_PROVIDERS)
    .refine((ids) => new Set(ids).size === ids.length, { message: "duplicate" }),
  scheduledStart: z.string().datetime({ offset: true }),
  paymentMethodId: z.string().uuid(),
});
export type QuoteRequestBody = z.infer<typeof QuoteRequestBody>;

/** POST /v1/provider/quotes/:taskId — teklif ver ya da güncelle (müşteri karar verene kadar) */
export const SubmitQuoteBody = z.object({
  /** hizmet veren fiyatı, komisyonsuz, kuruş */
  amountMinor: z
    .string()
    .regex(/^\d{1,8}$/)
    .refine((v) => BigInt(v) > 0n, { message: "positive" }),
  estimatedMinutes: z
    .number()
    .int()
    .min(15)
    .max(16 * 60),
  message: z.string().trim().max(1000).optional(),
  /** şirkette işi yapacak kişi; bireyselde kişinin kendisi */
  memberId: z.string().uuid().optional(),
});
export type SubmitQuoteBody = z.infer<typeof SubmitQuoteBody>;

export const QuoteStatus = z.enum(["sent", "accepted", "declined", "expired", "withdrawn"]);

/** Müşteri görünümü: fiyat Poji ücreti dahil (K-012) */
export const CustomerQuoteView = z.object({
  id: z.string().uuid(),
  providerId: z.string().uuid(),
  providerName: z.string(),
  providerSlug: z.string().nullable(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number(),
  price: ApiMoney,
  estimatedMinutes: z.number().nullable(),
  message: z.string().nullable(),
  validUntil: z.string().nullable(),
  status: QuoteStatus,
  createdAt: z.string(),
});
export type CustomerQuoteView = z.infer<typeof CustomerQuoteView>;

export const CustomerQuotesView = z.object({
  taskId: z.string().uuid(),
  status: z.string(),
  scheduledStart: z.string().nullable(),
  /** davet edilen hizmet verenlerin durumu (teklif bekleniyor / verdi / reddetti / süresi doldu) */
  invitations: z.array(
    z.object({ providerId: z.string().uuid(), providerName: z.string(), status: z.string() }),
  ),
  quotes: z.array(CustomerQuoteView),
});
export type CustomerQuotesView = z.infer<typeof CustomerQuotesView>;

/** Hizmet veren görünümü: kabulden önce adres yalnızca yerleşim yeri */
export const ProviderQuoteRequestView = z.object({
  taskId: z.string().uuid(),
  service: z.string(),
  status: z.string(),
  scheduledStart: z.string().nullable(),
  description: z.string().nullable(),
  answers: z.array(TaskAnswerView),
  addresses: z.array(TaskAddressView),
  invitationStatus: z.string(),
  /** teklif vermek için son an */
  respondBy: z.string().nullable(),
  /** benim teklifim (fiyat komisyonsuz) */
  myQuote: z
    .object({
      id: z.string().uuid(),
      amount: ApiMoney,
      estimatedMinutes: z.number().nullable(),
      message: z.string().nullable(),
      status: QuoteStatus,
      validUntil: z.string().nullable(),
    })
    .nullable(),
});
export type ProviderQuoteRequestView = z.infer<typeof ProviderQuoteRequestView>;
